import { FC } from 'react';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import cn from 'classnames';

import classes from 'src/styles/_commonClasses.module.scss';
import type { RichTextParsersConfig } from 'types/objectTypes';
import { capitalizeFirstLetter as capitalize } from 'lib/text.service';
import { mapBlocksHeadingWithId, mapBlocksParagraphWithExtraData } from 'lib/richTextMappers';
import { CommonRichText, DataLink } from 'components/Shared/SharedComponents';
import { Heading } from 'constants/enums';
import type { Link } from 'src/__generated__/graphqlTypes';
import { constructId } from 'src/shared/lib/section-extra-components';
import { clearLastInteractedPsychicCookie } from 'src/shared/lib/cookie';

import styles from './OrangeAlertBig.module.scss';

import type { IRichText, RichTextParam } from '../config/declarations';

const getParsersConfig = ({
  titleAlign,
  isOffset,
  pAlign,
  considerOnlyPresented,
  isContentAbove,
  isContentBelow,
  id,
}: RichTextParam): RichTextParsersConfig => {
  const titleAlignClass = classes[`titleAlign${capitalize(titleAlign)}`];
  const getHClasses = (specificClass?: string) => cn(
    styles.alertTitle,
    specificClass,
    titleAlignClass,
    { [styles.alertTitleOffset]: isOffset },
  );

  return ({
    [BLOCKS.EMBEDDED_ENTRY]: {
      classNames: cn(styles.alertButton, styles.alertButtonRichText),
      mapper: (node: any, map: Map<string, any>, className?: string) => {
        const { data: { target: { sys: { id: sysId } } } } = node;
        const link: Link = map.get(sysId);

        if (!link) return null;

        return (
          <DataLink
            id={constructId(id, '-embedded-link')}
            link={link}
            className={className}
            onClick={clearLastInteractedPsychicCookie}
          >
            {link.title}
          </DataLink>
        );
      },
    },
    [BLOCKS.HEADING_1]: {
      classNames: getHClasses(),
      mapper: mapBlocksHeadingWithId(constructId(id, '-heading'), Heading.FIRST),
    },
    [BLOCKS.HEADING_2]: {
      classNames: getHClasses(classes.alertTitleH2),
      mapper: mapBlocksHeadingWithId(constructId(id, '-heading')),
    },
    [BLOCKS.PARAGRAPH]: {
      classNames: cn(
        styles.alertParagraph,
        classes[`pAlign${capitalize(pAlign)}`],
        {
          [styles.alertParagraphMT]: isContentAbove && !considerOnlyPresented,
          [styles.alertParagraphMB]: isContentBelow && !considerOnlyPresented,
        },
      ),
      mapper: mapBlocksParagraphWithExtraData({ id: constructId(id, '-paragraph'), title: undefined }),
    },
    [MARKS.BOLD]: {
      classNames: classes.textBold,
    },
  });
};

const Title: FC<IRichText<RichTextParam>> = ({
  richText,
  config,
  id,
}) => {
  if (!richText) {
    return null;
  }

  const localConfig = { ...config, id };

  return (
    <CommonRichText
      content={richText}
      parsersConfig={getParsersConfig(localConfig)}
    />
  );
};

const Content = Title;

export { Content, Title };
